/**
 * The **boost ring** (`projects/token-command-menu.md`, the v20 ruling): the cells a unit reaches
 * only by Boosting, beyond its remaining move. Painted in the boost colour around the movement wash
 * while the unit is selected.
 *
 * Nothing is re-derived. The inner set is `reachableCells` on the same pre-activation preview the
 * movement wash reads (`previewIfControlled`; off-turn the wash reads a planning state instead,
 * `off-turn-planning` 2.2, and the ring stays empty because the reducer refuses an off-turn Boost); the outer set is `reachableCells` on the state the
 * real `boost` reducer produces from it. So the ring is empty exactly when the reducer would refuse
 * the Boost (no action left, already boosted, not this unit's turn), and its reach is whatever the
 * reducer's own move budget grows to.
 */
import { applyEncounter, moveBoostFold, reachableCells, type Cell, type Combatant, type EncounterState, type OptInActionRiderPrompt, type ProjectedEncounterState } from "@lancer-vtt/domain";
import { disengageSteps } from "@lancer-vtt/rules";
import { previewIfControlled } from "./activationPreview";

export function boostRingCells(args: { state: EncounterState; combatant: Combatant; actorId: string | null | undefined; isGm: boolean }): Set<string> | null {
  const { state, combatant, actorId, isGm } = args;
  const preview = previewIfControlled(state, combatant, { actorId, isGm });
  const unit = preview.combatants[combatant.id] ?? combatant;
  const inner = reachableCells(preview, unit);
  if (!inner) return null;
  const boosted = applyEncounter(preview, { type: "boost", actorId: actorId ?? "", combatantId: combatant.id });
  if (!boosted.ok) return null;
  const after = boosted.value.state;
  const outer = reachableCells(after, after.combatants[combatant.id] ?? unit);
  if (!outer) return null;
  const ring = new Set<string>();
  for (const key of outer) if (!inner.has(key)) ring.add(key);
  return ring;
}

/**
 * The Boost opt-in riders a PLAY drop of `combatantId` to `to` asks about (`move-family-opt-in-surface`
 * Phase 1): the unit's projected Boost riders (`optInActionRiders[id]["boost"]`, the Boost card's own
 * list) when the drop folds a Boost, else `null`. A drop inside base move, a refused Boost, or a unit
 * whose Boost carries no riders sends at once. The fold is {@link moveBoostFold} over
 * `previewIfControlled`, the same check `dispatchMove` runs, so the drag hint, the drop card and the
 * room agree on which drops Boost.
 */
export function boostDropRiders(args: {
  state: EncounterState;
  combatantId: string;
  to: Cell;
  path?: readonly Cell[];
  actorId: string | null | undefined;
  isGm: boolean;
}): OptInActionRiderPrompt[] | null {
  const { state, combatantId, to, path, actorId, isGm } = args;
  const projected: ProjectedEncounterState = state;
  const riders = projected.optInActionRiders?.[combatantId]?.["boost"];
  const combatant = state.combatants[combatantId];
  if (!riders || riders.length === 0 || !combatant || actorId == null) return null;
  const preview = previewIfControlled(state, combatant, { actorId, isGm });
  const fold = moveBoostFold(preview, { moverId: combatantId, to, actorId, ...(path !== undefined ? { path } : {}) });
  return fold.kind === "boost" ? riders : null;
}

/** A movement row the command menu previews on hover (`token-command-menu` Phase 3). */
export type MovePreviewVerb = "boost" | "disengage";
export type MovePreview = { unitId: string; verb: MovePreviewVerb };

/**
 * The cells `combatant` could reach after `verb` (the Boost or Disengage row's hover preview,
 * painted in the movement blue): `reachableCells` on the state the real reducer produces from
 * `state`. Boost runs the `boost` command (the budget grows by Speed); Disengage applies the steps
 * its resolution lands (`disengageSteps`: the Disengaged status, so engagement no longer stops the
 * move). `state` is the caller's movement state (`moveStateFor`: the unit's turn, the
 * pre-activation preview, or off-turn a planning state). `null` when the reducer refuses, or the
 * unit has no reach at all.
 */
export function moveVerbReach(args: { state: EncounterState; combatant: Combatant; verb: MovePreviewVerb; actorId: string }): Set<string> | null {
  const { state, combatant, verb, actorId } = args;
  const id = combatant.id;
  let after: EncounterState = state;
  if (verb === "boost") {
    const r = applyEncounter(state, { type: "boost", actorId, combatantId: id });
    if (!r.ok) return null;
    after = r.value.state;
  } else {
    const steps = disengageSteps(state, id);
    if (steps.length > 0) {
      const r = applyEncounter(state, { type: "apply_resolved", actorId, steps });
      if (!r.ok) return null;
      after = r.value.state;
    }
  }
  const unit = after.combatants[id];
  return unit ? reachableCells(after, unit) : null;
}
